import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../contexts/LanguageContext';
import styles from './Modal.module.css';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The app's own dialog (instead of window.confirm and ad-hoc overlays):
 * role="dialog", focus moves in and is kept inside, Escape or the backdrop
 * close it (unless `busy`), and focus returns where it was.
 */
const Modal = ({ open, title, onClose, children, footer, busy = false, size = 'md', placement = 'center', subtitle = null }) => {
  const { t } = useLanguage();
  const titleId = useId();
  const panelRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const panel = panelRef.current;
    const first = panel && panel.querySelector(`[data-autofocus], ${FOCUSABLE}`);
    if (first) first.focus();

    const onKey = (event) => {
      // A dialog opened from another one (a portal appended later) is the
      // one that answers: Escape must not close both, nor Tab jump between.
      const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
      if (panel && dialogs.length > 1 && dialogs[dialogs.length - 1] !== panel) return;
      if (event.key === 'Escape' && !busyRef.current) {
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const items = [...panel.querySelectorAll(FOCUSABLE)];
      if (items.length === 0) return;
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === items[0]) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        items[0].focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div
      className={`${styles.backdrop} ${placement === 'right' ? styles.backdropRight : ''}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div
        ref={panelRef}
        className={`${styles.panel} ${placement === 'right' ? styles.side : styles[size] || ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className={styles.header}>
          <div className={styles.headings}>
            <h2 id={titleId} className={styles.title}>{title}</h2>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
          </div>
          <button type="button" className={styles.close} onClick={onClose} disabled={busy} aria-label={t('Close')}>×</button>
        </div>
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
